export interface CurrencyInfo {
  code: string;
  name: string; // French
  country: string; // ISO 3166-1 alpha-2 for flag emoji
}

/**
 * Convert a 2-letter ISO 3166-1 alpha-2 country code to a flag emoji.
 * Each letter maps to a Regional Indicator Symbol: 0x1F1E6 = 🇦 (A),
 * so the offset is charCode('A') = 65. Input must be exactly 2 ASCII letters.
 */
export function countryToFlag(country: string): string {
  return Array.from(country.toUpperCase())
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join("");
}

/** Currencies shown directly in the Select dropdown (most common) */
export const COMMON_CURRENCY_CODES = [
  "USD",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
  "JPY",
  "CNY",
  "HKD",
  "SGD",
  "SEK",
  "NOK",
  "DKK",
  "NZD",
  "MXN",
  "BRL",
  "INR",
  "KRW",
  "AED",
  "SAR",
  "PLN",
  "CZK",
  "HUF",
  "TRY",
  "ZAR",
  "ILS",
];

/** Full ISO 4217 list used in the "More..." dialog */
export const ALL_CURRENCIES: CurrencyInfo[] = [
  { code: "AED", name: "Dirham émirati", country: "AE" },
  { code: "AFN", name: "Afghani afghan", country: "AF" },
  { code: "ALL", name: "Lek albanais", country: "AL" },
  { code: "AMD", name: "Dram arménien", country: "AM" },
  { code: "ANG", name: "Florin antillais néerlandais", country: "CW" },
  { code: "AOA", name: "Kwanza angolais", country: "AO" },
  { code: "ARS", name: "Peso argentin", country: "AR" },
  { code: "AUD", name: "Dollar australien", country: "AU" },
  { code: "AWG", name: "Florin arubais", country: "AW" },
  { code: "AZN", name: "Manat azerbaïdjanais", country: "AZ" },
  { code: "BAM", name: "Mark convertible de Bosnie", country: "BA" },
  { code: "BBD", name: "Dollar barbadien", country: "BB" },
  { code: "BDT", name: "Taka bangladais", country: "BD" },
  { code: "BGN", name: "Lev bulgare", country: "BG" },
  { code: "BHD", name: "Dinar bahreïnien", country: "BH" },
  { code: "BMD", name: "Dollar bermudien", country: "BM" },
  { code: "BND", name: "Dollar brunéien", country: "BN" },
  { code: "BOB", name: "Boliviano bolivien", country: "BO" },
  { code: "BRL", name: "Real brésilien", country: "BR" },
  { code: "BSD", name: "Dollar bahamien", country: "BS" },
  { code: "BWP", name: "Pula botswanais", country: "BW" },
  { code: "BYN", name: "Rouble biélorusse", country: "BY" },
  { code: "BZD", name: "Dollar bélizien", country: "BZ" },
  { code: "CAD", name: "Dollar canadien", country: "CA" },
  { code: "CHF", name: "Franc suisse", country: "CH" },
  { code: "CLP", name: "Peso chilien", country: "CL" },
  { code: "CNY", name: "Yuan renminbi", country: "CN" },
  { code: "COP", name: "Peso colombien", country: "CO" },
  { code: "CRC", name: "Colón costaricain", country: "CR" },
  { code: "CZK", name: "Couronne tchèque", country: "CZ" },
  { code: "DJF", name: "Franc djiboutien", country: "DJ" },
  { code: "DKK", name: "Couronne danoise", country: "DK" },
  { code: "DOP", name: "Peso dominicain", country: "DO" },
  { code: "DZD", name: "Dinar algérien", country: "DZ" },
  { code: "EGP", name: "Livre égyptienne", country: "EG" },
  { code: "ETB", name: "Birr éthiopien", country: "ET" },
  { code: "EUR", name: "Euro", country: "EU" },
  { code: "FJD", name: "Dollar fidjien", country: "FJ" },
  { code: "GBP", name: "Livre sterling", country: "GB" },
  { code: "GEL", name: "Lari géorgien", country: "GE" },
  { code: "GHS", name: "Cedi ghanéen", country: "GH" },
  { code: "GMD", name: "Dalasi gambien", country: "GM" },
  { code: "GTQ", name: "Quetzal guatémaltèque", country: "GT" },
  { code: "GYD", name: "Dollar guyanien", country: "GY" },
  { code: "HKD", name: "Dollar de Hong Kong", country: "HK" },
  { code: "HNL", name: "Lempira hondurien", country: "HN" },
  { code: "HRK", name: "Kuna croate", country: "HR" },
  { code: "HUF", name: "Forint hongrois", country: "HU" },
  { code: "IDR", name: "Roupie indonésienne", country: "ID" },
  { code: "ILS", name: "Shekel israélien", country: "IL" },
  { code: "INR", name: "Roupie indienne", country: "IN" },
  { code: "ISK", name: "Couronne islandaise", country: "IS" },
  { code: "JMD", name: "Dollar jamaïcain", country: "JM" },
  { code: "JOD", name: "Dinar jordanien", country: "JO" },
  { code: "JPY", name: "Yen japonais", country: "JP" },
  { code: "KES", name: "Shilling kényan", country: "KE" },
  { code: "KGS", name: "Som kirghiz", country: "KG" },
  { code: "KHR", name: "Riel cambodgien", country: "KH" },
  { code: "KRW", name: "Won sud-coréen", country: "KR" },
  { code: "KWD", name: "Dinar koweïtien", country: "KW" },
  { code: "KYD", name: "Dollar des îles Caïmans", country: "KY" },
  { code: "KZT", name: "Tenge kazakh", country: "KZ" },
  { code: "LBP", name: "Livre libanaise", country: "LB" },
  { code: "LKR", name: "Roupie srilankaise", country: "LK" },
  { code: "MAD", name: "Dirham marocain", country: "MA" },
  { code: "MDL", name: "Leu moldave", country: "MD" },
  { code: "MKD", name: "Denar macédonien", country: "MK" },
  { code: "MNT", name: "Tögrög mongol", country: "MN" },
  { code: "MOP", name: "Pataca macanaise", country: "MO" },
  { code: "MUR", name: "Roupie mauricienne", country: "MU" },
  { code: "MVR", name: "Rufiyaa maldivien", country: "MV" },
  { code: "MXN", name: "Peso mexicain", country: "MX" },
  { code: "MYR", name: "Ringgit malaisien", country: "MY" },
  { code: "MZN", name: "Metical mozambicain", country: "MZ" },
  { code: "NAD", name: "Dollar namibien", country: "NA" },
  { code: "NGN", name: "Naira nigérian", country: "NG" },
  { code: "NIO", name: "Córdoba nicaraguayen", country: "NI" },
  { code: "NOK", name: "Couronne norvégienne", country: "NO" },
  { code: "NPR", name: "Roupie népalaise", country: "NP" },
  { code: "NZD", name: "Dollar néo-zélandais", country: "NZ" },
  { code: "OMR", name: "Rial omanais", country: "OM" },
  { code: "PAB", name: "Balboa panaméen", country: "PA" },
  { code: "PEN", name: "Sol péruvien", country: "PE" },
  { code: "PGK", name: "Kina papouasien", country: "PG" },
  { code: "PHP", name: "Peso philippin", country: "PH" },
  { code: "PKR", name: "Roupie pakistanaise", country: "PK" },
  { code: "PLN", name: "Zloty polonais", country: "PL" },
  { code: "PYG", name: "Guaraní paraguayen", country: "PY" },
  { code: "QAR", name: "Riyal qatarien", country: "QA" },
  { code: "RON", name: "Leu roumain", country: "RO" },
  { code: "RSD", name: "Dinar serbe", country: "RS" },
  { code: "RUB", name: "Rouble russe", country: "RU" },
  { code: "SAR", name: "Riyal saoudien", country: "SA" },
  { code: "SEK", name: "Couronne suédoise", country: "SE" },
  { code: "SGD", name: "Dollar de Singapour", country: "SG" },
  { code: "SOS", name: "Shilling somalien", country: "SO" },
  { code: "SRD", name: "Dollar surinamais", country: "SR" },
  { code: "STD", name: "Dobra santoméen", country: "ST" },
  { code: "SVC", name: "Colón salvadorien", country: "SV" },
  { code: "SZL", name: "Lilangeni swatini", country: "SZ" },
  { code: "THB", name: "Baht thaïlandais", country: "TH" },
  { code: "TND", name: "Dinar tunisien", country: "TN" },
  { code: "TRY", name: "Livre turque", country: "TR" },
  { code: "TTD", name: "Dollar de Trinité-et-Tobago", country: "TT" },
  { code: "TWD", name: "Dollar de Taïwan", country: "TW" },
  { code: "TZS", name: "Shilling tanzanien", country: "TZ" },
  { code: "UAH", name: "Hryvnia ukrainienne", country: "UA" },
  { code: "UGX", name: "Shilling ougandais", country: "UG" },
  { code: "USD", name: "Dollar américain", country: "US" },
  { code: "UYU", name: "Peso uruguayen", country: "UY" },
  { code: "UZS", name: "Sum ouzbek", country: "UZ" },
  { code: "VND", name: "Dong vietnamien", country: "VN" },
  { code: "VUV", name: "Vatu vanuatuan", country: "VU" },
  { code: "WST", name: "Tālā samoan", country: "WS" },
  { code: "XAF", name: "Franc CFA d'Afrique centrale", country: "CM" },
  { code: "XCD", name: "Dollar des Caraïbes orientales", country: "AG" },
  { code: "XOF", name: "Franc CFA d'Afrique de l'Ouest", country: "SN" },
  { code: "YER", name: "Rial yéménite", country: "YE" },
  { code: "ZAR", name: "Rand sud-africain", country: "ZA" },
  { code: "ZMW", name: "Kwacha zambien", country: "ZM" },
];

export const ALL_CURRENCIES_BY_CODE = new Map(
  ALL_CURRENCIES.map((c) => [c.code, c]),
);
